import indexDbService from "../dbHelper";

/**
 * Formats order ID for KDS display
 * For local_id format like "102_1753462738390", returns "102_38390"
 * For regular IDs, returns the original ID
 * Also includes table name if the order has a table assigned
 * @param {Object} orderGeneral - The order object containing id
 * @returns {string} - The formatted order ID with optional table name prefix
 */
async function formatOrderIdForKds(orderGeneral) {
  
  let orderId = orderGeneral.id;
  
  // If id is not available (0, null, undefined), try to use local_id from the object first
  if (!orderId || orderId === 0) {
    if (orderGeneral.local_id) {
      orderId = orderGeneral.local_id;
    }
  }
  
  // If still not available, fetch from IndexedDB using local_id
  if (!orderId || orderId === 0) {
    try {
      // Full-scan read (NOT a status/recency subset): this is an id/local_id
      // lookup across every status, so the Phase 5 status/updated_at index
      // helpers do not apply. Left on getDbData intentionally.
      const orders = await indexDbService.getDbData("orders");
      
      // Find the order by id or try to find it by other means
      const order = orders.find(o => o.id === orderGeneral.id || o.local_id === orderGeneral.id);
      
      if (order) {
        // Use local_id if id is not available
        orderId = order.id && order.id !== 0 ? order.id : order.local_id;
      }
    } catch (error) {
      console.error("Error fetching from IndexedDB:", error);
    }
  }
  
  if (!orderId) {
    return "";
  }
  
  const orderIdStr = orderId.toString();
  
  // Format the order ID
  let formattedOrderId;
  // Check if it's a local_id format (contains underscore)
  if (orderIdStr.includes('_')) {
    const parts = orderIdStr.split('_');
    if (parts.length === 2) {
      const firstPart = parts[0];
      const secondPart = parts[1];
      
      // Get last 5 digits of the second part
      const lastFiveDigits = secondPart.slice(-5);
      formattedOrderId = `${firstPart}_${lastFiveDigits}`;
    } else {
      formattedOrderId = orderIdStr;
    }
  } else {
    // If not local_id format, return original
    formattedOrderId = orderIdStr;
  }
  
  // Try to get table name if the order has a table assigned
  let tablePrefix = "";
  const tableIdentifier = orderGeneral.table || orderGeneral.table_id;
  
  if (tableIdentifier) {
    try {
      // Dynamically import the store to avoid circular dependencies
      const store = await import("../../store/store").then(module => module.default);
      const table = store.getters["config/getTableById"](tableIdentifier);
      if (table && table.table_name) {
        tablePrefix = `${table.table_name} / `;
      }
    } catch (error) {
      console.error("Error fetching table name:", error);
    }
  }
  
  return tablePrefix + formattedOrderId;
}

/**
 * Gets table information for KDS payloads (optional).
 * Uses dynamic import of the Vuex store to avoid circular dependencies.
 * @param {Object} orderGeneral
 * @returns {Promise<{tableId: (string|null), tableName: (string|null)}>}
 */
async function getTableInfoForKds(orderGeneral) {
  const tableVal = orderGeneral?.table != null ? orderGeneral.table : orderGeneral?.table_id;
  const tableId = tableVal != null ? String(tableVal) : null;
  if (!tableId) return { tableId: null, tableName: null };

  try {
    const store = await import("../../store/store").then(module => module.default);
    const table = store.getters["config/getTableById"](tableVal);
    const tableName = table?.table_name ? String(table.table_name) : null;
    return { tableId, tableName };
  } catch (error) {
    console.error("Error fetching table info for KDS:", error);
    return { tableId, tableName: null };
  }
}

/**
 * Sends a complete order cancellation to the KDS
 * @param {Object} orderGeneral - General order information with items array
 * @param {Object} printer - The printer information
 * @returns {Promise<string>} - The response from the KDS
 */
export async function sendOrderCancellationToKds(orderGeneral, printer) {
  
  if (printer.is_kds) {
    
    // Check if items array exists
    if (!orderGeneral.items || !Array.isArray(orderGeneral.items)) {
      console.error("No items array in orderGeneral or it's not an array", orderGeneral);
      return Promise.resolve("No items array");
    }
    
    // For order cancellation, we need to send all items that were sent to the kitchen
    // and have not been sent to the KDS for cancellation yet
    const sentItems = orderGeneral.items.filter(item => 
      item.sent_to_kitchen && 
      !item.cancellation_sent_to_kds
    );
    
    if (sentItems.length > 0) {
      // Create an object with the filtered items
      const objWithSentItems = { items: sentItems };
      return sendCanceledItemsToKds(objWithSentItems, printer, orderGeneral);
    }
  }
  return Promise.resolve("No action taken");
}

export async function sendToKds (obj, printer, orderGeneral, receipt_kitchen_printed, reason = undefined) {
  
  // Special handling for order cancellation
  // When reason is "order cancelled", items haven't been voided yet, so we need to send all items that were sent to kitchen
  if (reason === "order cancelled") {
    const itemsSentToKitchen = obj.items.filter(item => 
      item.sent_to_kitchen && 
      !item.cancellation_sent_to_kds
    );
    
    if (itemsSentToKitchen.length > 0) {
      await sendCanceledItemsToKds({...obj, items: itemsSentToKitchen}, printer, orderGeneral);
    }
    return Promise.resolve();
  }
  
  // First, handle canceled items by sending them to the /cancel-items endpoint
  // Only send items that were previously sent to the kitchen AND are now canceled
  const canceledItems = obj.items.filter(item => 
    item.void_status === 1 && 
    item.sent_to_kitchen && 
    !item.cancellation_sent_to_kds
  );
  
  if (canceledItems.length > 0) {
    await sendCanceledItemsToKds({...obj, items: canceledItems}, printer, orderGeneral);
  }
  
  // Now handle new items that haven't been sent to the kitchen yet
  // Only include items that are not canceled/voided
  const newItems = obj.items.filter(item => 
    (item.void_status === 0 || item.void_status === null) && 
    !item.sent_to_kitchen && 
    item.printers && item.printers.length > 0
  );
  
  
  // If there are new items, send them to the KDS
  if (newItems.length > 0) {
    // Create a new object with only the new items
    const newItemsObj = {...obj, items: newItems};
    
    // Check if the KDS has local_ip and is of kds_type 'printer'
    if (printer.is_kds && printer.kds_type === "printer" && printer.local_ip) {
      // Handle IP-based KDS
      return sendToIpBasedKds(newItemsObj, printer, orderGeneral);
    } else if (printer.is_kds) {
      // Handle cloud-based KDS
      return sendToCloudKds(newItemsObj, printer, orderGeneral, false); // Always send new items as non-updates
    }
  }
  
  return Promise.resolve();

}

async function sendToIpBasedKds(obj, printer, orderGeneral) {
  try {
    
    // Format the payload according to the specified structure
    // Get the dining option name based on the type
    let diningOption = "";
    let diningOptionType = null;
    
    // First determine which dining option type to use
    
    if (orderGeneral.dining_option_type !== undefined && orderGeneral.dining_option_type !== null) {
      diningOptionType = parseInt(orderGeneral.dining_option_type.toString());
    } else if (orderGeneral.dining_option !== undefined && orderGeneral.dining_option !== null) {
      diningOptionType = parseInt(orderGeneral.dining_option.toString());
    }
    
    // Get dining option EXACTLY the same way as in Order.vue (lines 1248-1251)
    if (orderGeneral.dining_option) {
      // If dining_option is already set in the order, use it directly
      diningOption = orderGeneral.dining_option;
    } else {
      // If no dining option in the order, try to look it up from terminal data
      try {
        const terminal = await indexDbService.getTerminal();
        
        
        // This is EXACTLY how Order.vue does it (line 1250)
        if (terminal && terminal.dining_options && Array.isArray(terminal.dining_options)) {
          // First, if we have a dining_option_type, use that to find the name
          if (diningOptionType !== null) {
            const matchingOption = terminal.dining_options.find(option => option.id === diningOptionType);
            if (matchingOption && matchingOption.name) {
              diningOption = matchingOption.name;
            }
          }
          
          // If no dining option found and we have a default_dining_option, use that
          // This is EXACTLY how Order.vue handles default dining options
          if (!diningOption && terminal.default_dining_option) {
            // First set the dining_option_type to the default
            const defaultDiningOptionType = terminal.default_dining_option;
            
            // Then find the name using the same approach as Order.vue
            const defaultOption = terminal.dining_options.find(option => option.id === defaultDiningOptionType);
            if (defaultOption && defaultOption.name) {
              diningOption = defaultOption.name;
            }
          }
        }
      } catch (error) {
        console.error("Error getting dining option name from terminal data:", error);
      }
    }
    
    const { tableId, tableName } = await getTableInfoForKds(orderGeneral);

    // Fetch all products once to avoid repeated DB calls
    let allProducts = [];
    try {
      allProducts = await indexDbService.getDbData("products");
    } catch (e) {
      console.error("Error fetching products from DB:", e);
    }

    const isUpstash = !!(printer.local_ip && printer.local_ip.includes("redis"));

    const payload = {
      "orderId": await formatOrderIdForKds(orderGeneral),
      "timestamp": new Date().toISOString(),
      "diningOption": diningOption,
      "source": "POS System",
      ...(tableId ? { "tableId": tableId } : {}),
      ...(tableName ? { "tableName": tableName } : {}),
      "items": await Promise.all(obj.items.map(async item => {
        // Prefer the explicit product id if present; fall back to parsing item.id (format: productId_index_timestamp)
        let productId = null;
        const directProductId = item && item.product != null ? parseInt(item.product) : null;
        if (Number.isFinite(directProductId) && directProductId > 0) {
          productId = directProductId;
        } else {
          const itemIdStr = item && item.id ? String(item.id) : '';
          if (itemIdStr && itemIdStr.includes('_')) {
            const parsed = parseInt(itemIdStr.split('_')[0]);
            if (Number.isFinite(parsed) && parsed > 0) {
              productId = parsed;
            }
          }
        }
        
        // Find product in allProducts
        const product = productId ? allProducts.find(p => p.id === productId) : null;
        
        // Determine item name (use kitchen_display_name if available)
        let itemName = item.name;
        if (product && product.kitchen_display_name) {
          itemName = product.kitchen_display_name;
        }

        // Enhanced category lookup with product database check
        let itemCategory = "";
        
        // First try using the item's own category properties
        if (item.category_name) {
          itemCategory = item.category_name;
        } else if (item.category) {
          itemCategory = item.category;
        } else if (product) {
          // If no category on the item, use the fetched product
          try {
            // Get the category information
            if (product.category_name) {
              itemCategory = product.category_name;
            } else if (product.category) {
              // If the product has a category ID, look up the category name
              const categoryId = product.category;
              const categories = await indexDbService.getDbData("categories");
              const category = categories.find(c => c.id === categoryId);
              
              if (category && category.name) {
                itemCategory = category.name;
              }
            }
          } catch (error) {
            console.error(`Error looking up category for product ${productId}:`, error);
          }
        }
        
        let modifierGroups = [];
        if (isUpstash) {
          const activeMods = (item.modifiers || []).filter(mod => !mod.void_status);
          const groupsById = new Map();

          for (const mod of activeMods) {
            const groupId = mod.group;
            if (!groupsById.has(groupId)) {
              const productGroup = product && Array.isArray(product.modifier_groups)
                ? product.modifier_groups.find(g => g && g.id === groupId)
                : null;

              const groupKitchenDisplayName = productGroup ? productGroup.kitchen_display_name : null;
              const groupName = (groupKitchenDisplayName != null && groupKitchenDisplayName !== "")
                ? groupKitchenDisplayName
                : (productGroup ? productGroup.name : "");

              groupsById.set(groupId, {
                modifier_group_id: groupId != null ? groupId.toString() : "",
                modifier_group_name: groupName || "",
                ...(groupKitchenDisplayName != null ? { kitchen_display_name: groupKitchenDisplayName } : {}),
                modifiers: []
              });
            }

            const productGroup = product && Array.isArray(product.modifier_groups)
              ? product.modifier_groups.find(g => g && g.id === groupId)
              : null;
            const productModifier = productGroup && Array.isArray(productGroup.modifiers)
              ? productGroup.modifiers.find(m => m && m.id === mod.oryginal)
              : null;

            const modKitchenDisplayName = productModifier ? productModifier.kitchen_display_name : null;
            const modName = (modKitchenDisplayName != null && modKitchenDisplayName !== "")
              ? modKitchenDisplayName
              : (mod.name || (productModifier ? productModifier.name : ""));

            groupsById.get(groupId).modifiers.push({
              modifier_id: mod.oryginal != null ? mod.oryginal.toString() : "",
              modifier_name: modName || "",
              ...(modKitchenDisplayName != null ? { kitchen_display_name: modKitchenDisplayName } : {})
            });
          }

          modifierGroups = Array.from(groupsById.values());
        }

        return {
          "id": item.id ? item.id.toString() : "",
          "name": itemName,
          "quantity": item.quantity,
          "notes": item.notes || "",
          "modifiers": isUpstash ? modifierGroups : (item.modifiers || []).map(mod => mod.name),
          "category": itemCategory,
          "isCompleted": false
        };
      })),
      "isCompleted": false
    };
    
    
    // Only add customer object if a customer is selected
    if (orderGeneral.customer && 
        (orderGeneral.customer.id || orderGeneral.customer.first_name || orderGeneral.customer.last_name)) {
      const customer = orderGeneral.customer;
      payload.customer = {
        "name": customer.first_name && customer.last_name ? 
          `${customer.first_name} ${customer.last_name}` : 
          (customer.first_name || customer.last_name || ""),
        "id": customer.id ? customer.id.toString() : ""
      };
    }

    let url;
    if (isUpstash) {
      // Get baseUrl from localStorage and extract subdomain
      let subdomain = "";
      try {
        const baseUrl = localStorage.getItem("baseUrl") || "";
        // Example: https://dev2.biyo.co
        const match = baseUrl.match(/^https?:\/\/([^.]+)\.biyo\.co/);
        if (match && match[1]) {
          subdomain = match[1];
        }
      } catch (e) {
        console.error("Could not parse subdomain from baseUrl", e);
      }
      url = `${process.env.VUE_APP_UPSTASH_URL}/rpush/${subdomain}-${printer.id}`;
    } else {
      const baseUrl = printer.local_ip.startsWith("http") ? printer.local_ip : `http://${printer.local_ip}`;
      url = `${baseUrl}/orders`;
    }

    // Set up request options - always use POST for IP-based KDS
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    };
    if (isUpstash) {
      requestOptions.headers["Authorization"] = "Bearer AWrYAAIjcDEzYjE2MDM2MDQ5ZGE0NzZlYjYwYmFkNWYwZWJlOGZhZnAxMA";
    }


    // Send the request
    const response = await fetch(url, requestOptions);
    const result = await response.text();
    
    // If the request was successful, mark the items as sent to the kitchen
    if (response.ok) {
      try {
        // Import the store dynamically to avoid circular dependencies
        const store = await import("../../store/store").then(module => module.default);
        const order = store.getters["orders/getOrderByIdOrLocalId"](orderGeneral.id);
        
        if (order && order.items) {
          let updated = false;
          
          // Update the sent_to_kitchen flag for each item
          obj.items.forEach(sentItem => {
            const itemIndex = order.items.findIndex(item => item.id === sentItem.id);
            if (itemIndex !== -1) {
              // Only mark as sent if not already marked and not canceled
              if (!order.items[itemIndex].sent_to_kitchen && order.items[itemIndex].void_status !== 1) {
                order.items[itemIndex].sent_to_kitchen = true;
                order.items[itemIndex].has_kitchen_updates = false;
                updated = true;
              }
            }
          });
          
          // Save the updated order if changes were made
          if (updated) {
            store.dispatch("orders/saveOrder", order);
            store.dispatch("orders/enqueueOrderSync", { order, trigger: "kds_ip_send" })
              .then(() => store.dispatch("orders/drainOrderSyncOutbox"))
              .catch(() => {});
          }
        }
      } catch (storeError) {
        console.error("Error updating store after sending to KDS:", storeError);
        // Continue execution even if store update fails
      }
    }
    
    return result;
  } catch (error) {
    console.error("Error sending to IP-based KDS:", error);
    throw error;
  }
}

/**
 * Sends canceled items to the KDS
 * @param {Object} obj - The printer object containing items
 * @param {Object} printer - The printer information
 * @param {Object} orderGeneral - General order information
 * @returns {Promise<string>} - The response from the KDS
 */
/**
 * Direct function to send cancel-items to IP-based KDS
 * @param {string} printerIp - The IP address of the KDS printer
 * @param {string} orderId - The order ID
 * @param {Array} itemIds - Array of item IDs to cancel
 * @returns {Promise<string>} - The response from the KDS
 */
export async function sendCancelItemsDirectly(printerIp, orderId, itemIds) {
  try {
    
    // Create the payload
    const payload = {
      "orderId": await formatOrderIdForKds({ id: orderId }),
      "itemIds": itemIds.map(id => id.toString())
    };
    
    // Set up request options
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    };
    
    // Construct the URL
    const baseUrl = printerIp.startsWith("http") ? printerIp : `http://${printerIp}`;
    const url = `${baseUrl}/cancel-items`;
    
    
    // Send the request
    const response = await fetch(url, requestOptions);
    const result = await response.text();
    
    // If successful, mark these items as sent to KDS for cancellation in the store
    if (response.ok) {
      try {
        // Import the store dynamically to avoid circular dependencies
        const store = await import("../../store/store").then(module => module.default);
        const order = store.getters["orders/getOrderByIdOrLocalId"](orderId);
        
        if (order && order.items) {
          let updated = false;
          
          // Update the cancellation_sent_to_kds flag for each item
          itemIds.forEach(itemId => {
            const itemIndex = order.items.findIndex(item => item.id === itemId);
            if (itemIndex !== -1) {
              order.items[itemIndex].cancellation_sent_to_kds = true;
              updated = true;
            }
          });
          
          // Save the updated order if changes were made
          if (updated) {
            store.dispatch("orders/saveOrder", order);
            store.dispatch("orders/enqueueOrderSync", { order, trigger: "kds_cancel_send" })
              .then(() => store.dispatch("orders/drainOrderSyncOutbox"))
              .catch(() => {});
          }
        }
      } catch (storeError) {
        console.error("Error updating store after cancel-items:", storeError);
        // Continue execution even if store update fails
      }
    }
    
    return result;
  } catch (error) {
    console.error("Error in direct cancel-items call:", error);
    throw error;
  }
}

async function sendCanceledItemsToKds(obj, printer, orderGeneral) {
  try {
    // For both individual item cancellation and full order cancellation, we use the same endpoint
    // but with different item arrays
    
    // For individual canceled items
    
    // Extract item IDs and filter out any empty strings
    const itemIds = obj.items
      .map(item => item.id ? item.id.toString() : "")
      .filter(id => id !== "");
      
    
    const payload = {
      "orderId": await formatOrderIdForKds(orderGeneral),
      "itemIds": itemIds
    };

    // Determine the endpoint based on KDS type
    if (printer.is_kds && printer.kds_type === "printer" && printer.local_ip) {
      // For IP-based KDS
      let url;
      let isUpstash = false;
      if (printer.local_ip && printer.local_ip.includes("redis")) {
        // Get baseUrl from localStorage and extract subdomain
        let subdomain = "";
        try {
          const baseUrl = localStorage.getItem("baseUrl") || "";
          // Example: https://dev2.biyo.co
          const match = baseUrl.match(/^https?:\/\/([^.]+)\.biyo\.co/);
          if (match && match[1]) {
            subdomain = match[1];
          }
        } catch (e) {
          console.error("Could not parse subdomain from baseUrl", e);
        }
        url = `${process.env.VUE_APP_UPSTASH_URL}/rpush/${subdomain}-${printer.id}-cancel`;
        isUpstash = true;
      } else {
        const baseUrl = printer.local_ip.startsWith("http") ? printer.local_ip : `http://${printer.local_ip}`;
        url = `${baseUrl}/cancel-items`;
      }

      // Set up request options
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      };
      if (isUpstash) {
        requestOptions.headers["Authorization"] = "Bearer AWrYAAIjcDEzYjE2MDM2MDQ5ZGE0NzZlYjYwYmFkNWYwZWJlOGZhZnAxMA";
      }


      // Send the request
      const response = await fetch(url, requestOptions);
      const result = await response.text();
      
      // Mark these items as having been sent to KDS for cancellation
      // Note: We don't update the store here to avoid race conditions with the main cancellation flow
      // The main cancellation process (cancelOrderProcessing) will handle updating the order state
      if (response.ok) {
        // Update the items in the current order object if it's still in memory
        // This avoids race conditions with store updates
        if (orderGeneral.items) {
          obj.items.forEach(canceledItem => {
            const itemIndex = orderGeneral.items.findIndex(item => item.id === canceledItem.id);
            if (itemIndex !== -1) {
              orderGeneral.items[itemIndex].cancellation_sent_to_kds = true;
            }
          });
        }
      } else {
        console.error("KDS cancellation failed with response:", result);
      }
      
      return result;
    } else if (printer.is_kds) {
      // For cloud-based KDS, implement cloud-specific cancellation logic here
      // This would depend on the cloud KDS API specifications
      return Promise.resolve("Cloud KDS cancellation not implemented");
    }

    return Promise.resolve("Not a KDS printer");
  } catch (error) {
    console.error("Error sending canceled items to KDS:", error);
    throw error;
  }
}

async function sendToCloudKds(obj, printer, orderGeneral, isUpdate = false) {
  try {
    const myHeaders = new Headers();
    myHeaders.append("x-integration-token", printer.token);
    myHeaders.append("x-location-id", printer.kds_location);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("x-device-ids", printer.kds_device || "all");

    // Get the dining option name based on the type
    let diningOption = "";
    let diningOptionType = null;
    
    // First determine which dining option type to use
    
    if (orderGeneral.dining_option_type !== undefined && orderGeneral.dining_option_type !== null) {
      diningOptionType = parseInt(orderGeneral.dining_option_type.toString());
    } else if (orderGeneral.dining_option !== undefined && orderGeneral.dining_option !== null) {
      diningOptionType = parseInt(orderGeneral.dining_option.toString());
    }
    
    // Get dining option EXACTLY the same way as in Order.vue (lines 1248-1251)
    if (orderGeneral.dining_option) {
      // If dining_option is already set in the order, use it directly
      diningOption = orderGeneral.dining_option;
    } else {
      // If no dining option in the order, try to look it up from terminal data
      try {
        const terminal = await indexDbService.getTerminal();
        
        
        // This is EXACTLY how Order.vue does it (line 1250)
        if (terminal && terminal.dining_options && Array.isArray(terminal.dining_options)) {
          // First, if we have a dining_option_type, use that to find the name
          if (diningOptionType !== null) {
            const matchingOption = terminal.dining_options.find(option => option.id === diningOptionType);
            if (matchingOption && matchingOption.name) {
              diningOption = matchingOption.name;
            }
          }
          
          // If no dining option found and we have a default_dining_option, use that
          // This is EXACTLY how Order.vue handles default dining options
          if (!diningOption && terminal.default_dining_option) {
            // First set the dining_option_type to the default
            const defaultDiningOptionType = terminal.default_dining_option;
            
            // Then find the name using the same approach as Order.vue
            const defaultOption = terminal.dining_options.find(option => option.id === defaultDiningOptionType);
            if (defaultOption && defaultOption.name) {
              diningOption = defaultOption.name;
            }
          }
        }
      } catch (error) {
        console.error("Error getting dining option name from terminal data:", error);
      }
    }
    
    const { tableId, tableName } = await getTableInfoForKds(orderGeneral);

    // Fetch all products once to avoid repeated DB calls
    let allProducts = [];
    try {
      allProducts = await indexDbService.getDbData("products");
    } catch (e) {
      console.error("Error fetching products from DB:", e);
    }

    const raw = {
      "id": String(orderGeneral.id || orderGeneral.local_id || ""),
      "name": `Order ${String(orderGeneral.id || orderGeneral.local_id || "")}`,
      "terminal": orderGeneral.related_terminal.toString(),
      "time": orderGeneral.updated_at,
      "mode": diningOption,
      ...(tableId ? { "tableId": tableId } : {}),
      ...(tableName ? { "tableName": tableName } : {}),
      "items": []
    };
    
    
    // Use Promise.all to handle async operations for each item
    await Promise.all(obj.items.map(async (item) => {
      // Prefer the explicit product id if present; fall back to parsing item.id (format: productId_index_timestamp)
      let productId = null;
      const directProductId = item && item.product != null ? parseInt(item.product) : null;
      if (Number.isFinite(directProductId) && directProductId > 0) {
        productId = directProductId;
      } else {
        const itemIdStr = item && item.id ? String(item.id) : '';
        if (itemIdStr && itemIdStr.includes('_')) {
          const parsed = parseInt(itemIdStr.split('_')[0]);
          if (Number.isFinite(parsed) && parsed > 0) {
            productId = parsed;
          }
        }
      }
      
      // Find product in allProducts
      const product = productId ? allProducts.find(p => p.id === productId) : null;
      
      // Determine item name (use kitchen_display_name if available)
      let itemName = item.name;
      if (product && product.kitchen_display_name) {
        itemName = product.kitchen_display_name;
      }

      // Enhanced category lookup with product database check
      let itemCategory = "";
      
      // First try using the item's own category properties
      if (item.category_name) {
        itemCategory = item.category_name;
      } else if (item.category) {
        itemCategory = item.category;
      } else if (product) {
        // If no category on the item, use the fetched product
        try {
          // Get the category information
          if (product.category_name) {
            itemCategory = product.category_name;
          } else if (product.category) {
            // If the product has a category ID, look up the category name
            const categoryId = product.category;
            const categories = await indexDbService.getDbData("categories");
            const category = categories.find(c => c.id === categoryId);
            
            if (category && category.name) {
              itemCategory = category.name;
            }
          }
        } catch (error) {
          console.error(`Cloud KDS: Error looking up category for product ${productId}:`, error);
        }
      }
      
      // Add the item to the raw.items array
      raw["items"].push({
        id: item.id ? String(item.id) : "",
        name: itemName,
        qty: item.quantity,
        category: itemCategory,
        mods: item.modifiers.map((m) => {
          return { name: m.name };
        })
      });
    }));
    

    const requestOptions = {
      method: isUpdate ? "PUT" : "POST",
      headers: myHeaders,
      body: JSON.stringify(raw),
      redirect: "follow"
    };

    const url = "https://demo-integrations-api.ftservices.cloud/integrators/kds-orders";
    const response = await fetch(url, requestOptions);
    const result = await response.text();
    
    // If the request was successful, mark the items as sent to the kitchen
    if (response.ok) {
      try {
        // Import the store dynamically to avoid circular dependencies
        const store = await import("../../store/store").then(module => module.default);
        const order = store.getters["orders/getOrderByIdOrLocalId"](orderGeneral.id);
        
        if (order && order.items) {
          let updated = false;
          
          // Update the sent_to_kitchen flag for each item
          obj.items.forEach(sentItem => {
            const itemIndex = order.items.findIndex(item => item.id === sentItem.id);
            if (itemIndex !== -1) {
              // Only mark as sent if not already marked and not canceled
              if (!order.items[itemIndex].sent_to_kitchen && order.items[itemIndex].void_status !== 1) {
                order.items[itemIndex].sent_to_kitchen = true;
                order.items[itemIndex].has_kitchen_updates = false;
                updated = true;
              }
            }
          });
          
          // Save the updated order if changes were made
          if (updated) {
            store.dispatch("orders/saveOrder", order);
            store.dispatch("orders/enqueueOrderSync", { order, trigger: "kds_cloud_send" })
              .then(() => store.dispatch("orders/drainOrderSyncOutbox"))
              .catch(() => {});
          }
        }
      } catch (storeError) {
        console.error("Error updating store after sending to Cloud KDS:", storeError);
        // Continue execution even if store update fails
      }
    }
    
    return result;
  } catch (error) {
    console.error("Error sending to cloud KDS:", error);
    throw error;
  }
}
